import React from "react";
import { Navigate } from "react-router-dom";
import { useAbility } from "../config/abilityContext";
import { Actions, Modules } from "../config/permissions";

type PermissionCheck = { action: Actions; subject: Modules };

type SinglePermissionProps = {
  element: React.ReactElement;
  action: Actions;
  subject: Modules;
  fallbackPath?: string;
};

type MultiPermissionProps = {
  element: React.ReactElement;
  permissions: PermissionCheck[];
  fallbackPath?: string;
};

// Back-compat: supports either (action+subject) OR (permissions[])
type ProtectedRouteProps = SinglePermissionProps | MultiPermissionProps;

const ProtectedRoute: React.FC<ProtectedRouteProps> = (props) => {
  const ability = useAbility();
  const fallback = ("fallbackPath" in props && props.fallbackPath) || "/no-access";

  // New style: permissions array => allow if ANY matches
  if ("permissions" in props && Array.isArray(props.permissions)) {
    const ok = props.permissions.some((p) => ability.can(p.action, p.subject));
    return ok ? props.element : <Navigate to={fallback} replace />;
  }

  // Old style: single action + subject (kept for all existing routes)
  if ("action" in props && "subject" in props) {
    const ok = ability.can(props.action, props.subject);
    return ok ? props.element : <Navigate to={fallback} replace />;
  }

  // If misused, block by default
  return <Navigate to={fallback} replace />;
};

export default ProtectedRoute;
